import WebFont from 'webfontloader';
import { useFonts } from '@/composables/api/useFonts';
import { useIframeDetection } from '@/composables/useIframeDetection';

const fontsApi = useFonts();
const systemFonts = [
    'Arial',
    'Verdana',
    'Tahoma',
    'Trebuchet MS',
    'Times New Roman',
    'Georgia',
    'Garamond',
    'Courier New',
    'Brush Script MT',
    'Impact'
];

const systemFontSet = new Set(systemFonts.map((font) => font.toLowerCase()));

// Nombre de familles proposees dans le FontPicker en repli LEGACY (Google direct).
// Le chemin backend renvoie sa propre liste (system + shared + tenant + google
// baseline) et n'utilise pas ce plafond.
const PICKER_FAMILY_COUNT = 100;

// Familles connues du catalogue (backend ou, en repli, Google), renseignees par
// `loadFonts`. Sert a `normalizeFamily` pour departager une liste CSS.
let knownFamilies = new Set<string>();

// Source de chaque famille (system | shared | tenant | google), renseignee par le
// descripteur backend. `ensureFontsLoaded`/l'export s'en servent pour construire
// l'URL `/fonts/file?source=…`. Une famille inconnue est traitee comme `google`
// (le backend proxifie n'importe quelle Google Font a la demande).
const familySources = new Map<string, string>();

// Familles deja resolues (chargees ou definitivement indisponibles) et
// chargements en cours, pour ne jamais redemander deux fois la meme.
const resolvedFamilies = new Set<string>();
const inflightFamilies = new Map<string, Promise<void>>();

// Polices IMPORTÉES par l'utilisateur (upload TTF/OTF) : déjà injectées dans le
// document via FontFace → jamais à télécharger côté backend et toujours
// considérées « résolues ». Voir useCustomFonts.
const customFamilies = new Set<string>();

/**
 * FEATURE FLAG bibliotheque de polices (bascule backend ↔ legacy Google-direct).
 *
 * On passe par le backend (proxy RGPD) DES QUE `VITE_BACK_URL` est configure,
 * SAUF si `VITE_FONT_LIBRARY === 'false'` (kill switch explicite). Sans backend
 * connu, on retombe sur l'ancien comportement Google-direct pour ne rien casser
 * avant le deploiement backend.
 */
function fontLibraryEnabled(): boolean {
    const env = (typeof window !== 'undefined' && window.env) || ({} as Record<string, unknown>);
    if (String(env.VITE_FONT_LIBRARY) === 'false') {
        return false;
    }
    return Boolean(getBackendUrl());
}

/** URL du backend (window.env), normalisee en https (meme motif que ValidationSheet). */
function getBackendUrl(): string {
    let url = ((typeof window !== 'undefined' && (window.env?.VITE_BACK_URL as string | undefined)) || '') as string;
    if (url && !/^https?:\/\//.test(url)) {
        url = `https://${url}`;
    }
    return url.replace(/\/+$/, '');
}

/**
 * Identification du tenant : meme mecanisme que tous les autres appels backend du
 * FO (produits/themes/cliparts via `useFetchInstance`) → header `originUrl` derive
 * de l'hote par `useIframeDetection`.
 */
function tenantHeaders(): Record<string, string> {
    try {
        return { originUrl: useIframeDetection().getOriginUrl() };
    } catch {
        return { originUrl: typeof window !== 'undefined' ? window.location.href : '' };
    }
}

interface FontDescriptor {
    family: string;
    source?: string;
    category?: string;
    format?: string | null;
    fileUrl?: string | null;
    weights?: string[] | null;
    id?: string | null;
}

/** Memorise la source (et connaissance) d'un lot de descripteurs backend. */
function recordDescriptors(descriptors: FontDescriptor[]): string[] {
    const families: string[] = [];
    for (const d of descriptors) {
        const family = (d?.family || '').trim();
        if (!family) {
            continue;
        }
        families.push(family);
        knownFamilies.add(family);
        if (d.source) {
            familySources.set(family, d.source);
        }
    }
    return families;
}

/** Déclare une famille comme police custom (importée) : résolue, jamais backend. */
export function registerCustomFamily(family: string): void {
    const name = (family || '').trim();
    if (!name) {
        return;
    }
    customFamilies.add(name);
    resolvedFamilies.add(name); // déjà disponible via FontFace
}

/** Retire une famille custom (suppression d'un import). */
export function unregisterCustomFamily(family: string): void {
    const name = (family || '').trim();
    customFamilies.delete(name);
    resolvedFamilies.delete(name);
}

/** True si la famille est une police importée par l'utilisateur. */
export function isCustomFamily(family: string): boolean {
    return customFamilies.has((family || '').trim());
}

/** True si la famille est une police système (aucun fichier à charger). */
export function isSystemFamily(family: string): boolean {
    return systemFontSet.has((family || '').trim().toLowerCase());
}

/** Source connue d'une famille (défaut `google` : le backend proxifie à la demande). */
export function getFamilySource(family: string): string {
    return familySources.get((family || '').trim()) || 'google';
}

// Filet de securite : si le chargement d'une FontFace (ou WebFont en legacy) ne se
// resout jamais (reseau coupe, CSS bloque), on ne fige pas le rendu du design.
const FONT_LOAD_TIMEOUT_MS = 8000;

/**
 * Recupere la liste des polices proposees a l'utilisateur.
 *
 * Chemin BACKEND (par defaut) : `GET {back}/fonts` (catalogue unifie system +
 * shared activees + tenant + baseline Google) — aucune requete client vers Google
 * (RGPD). Le telechargement des fichiers reste dirige par le contenu, via
 * `ensureFontsLoaded`.
 *
 * Chemin LEGACY (repli si backend absent / flag off) : ancien appel direct
 * Google `webfonts/v1` — conserve pour ne rien casser avant le deploiement.
 */
export async function loadFonts(apiKey: string): Promise<string[]> {
    if (fontLibraryEnabled()) {
        return loadFontsFromBackend();
    }
    return loadFontsFromGoogle(apiKey);
}

async function loadFontsFromBackend(): Promise<string[]> {
    let fonts: string[] = [...systemFonts];
    try {
        const resp = await fetch(`${getBackendUrl()}/fonts`, { headers: tenantHeaders() });
        if (!resp.ok) {
            console.error('[fonts] backend catalogue KO', resp.status);
            return fonts.sort();
        }
        const data = (await resp.json()) as FontDescriptor[];
        const families = recordDescriptors(Array.isArray(data) ? data : []);
        fonts = [...new Set([...systemFonts, ...families])];
    } catch (error) {
        console.error('[fonts] échec du chargement du catalogue backend:', error);
    }
    return fonts.sort();
}

async function loadFontsFromGoogle(apiKey: string): Promise<string[]> {
    let fonts: string[] = [...systemFonts];
    try {
        const { data, error } = await fontsApi.fetchGoogleFonts(apiKey);
        if (error.value) {
            console.error('Error fetching Google Fonts');
            return fonts.sort();
        }
        if (data.value) {
            const allFamilies: string[] = (data.value.items ?? []).map((font: { family: string }) => font.family);
            knownFamilies = new Set(allFamilies);
            fonts = [...new Set([...systemFonts, ...allFamilies.slice(0, PICKER_FAMILY_COUNT)])];
        }
    } catch (error) {
        console.error('Error loading fonts:', error);
    }

    return fonts.sort();
}

/**
 * Recherche de police via Meilisearch (proxy backend `GET {back}/fonts/search?q=`).
 * Renvoie la liste des familles correspondantes et memorise leur source pour que
 * les apercus se chargent avec le bon `/fonts/file?source=…`. `[]` si backend
 * indisponible/flag off (l'appelant retombe alors sur un filtre client).
 */
export async function searchFonts(query: string, limit?: number): Promise<string[]> {
    const q = (query || '').trim();
    if (!q || !fontLibraryEnabled()) {
        return [];
    }
    try {
        const params = new URLSearchParams({ q });
        if (limit) {
            params.set('limit', String(limit));
        }
        const resp = await fetch(`${getBackendUrl()}/fonts/search?${params.toString()}`, { headers: tenantHeaders() });
        if (!resp.ok) {
            return [];
        }
        const data = (await resp.json()) as FontDescriptor[];
        return recordDescriptors(Array.isArray(data) ? data : []);
    } catch {
        return [];
    }
}

/**
 * Normalise une valeur `fontFamily` issue d'un JSON Fabric.
 *
 * Ces valeurs sont parfois des listes CSS ("Dynalight-Regular, Dynalight") dont
 * seule une entree correspond a une famille connue. On retient donc la premiere
 * entree connue, sinon la premiere tout court.
 */
export function normalizeFamily(rawFamily: string): string {
    const candidates = rawFamily
        .split(',')
        .map((part) => part.trim().replace(/^["']|["']$/g, ''))
        .filter(Boolean);

    if (candidates.length === 0) {
        return '';
    }
    // Police IMPORTÉE : nom EXACT, jamais réécrit (elle n'est ni dans le catalogue
    // ni suffixée) — sinon on casserait la résolution FontFace/export.
    const custom = candidates.find((candidate) => customFamilies.has(candidate));
    if (custom) {
        return custom;
    }
    return candidates.find((candidate) => knownFamilies.has(candidate)) ?? candidates[0];
}

/**
 * Parcourt un JSON de design (theme, sauvegarde panier, backup) et remonte
 * toutes les familles de polices referencees.
 *
 * Le parcours est recursif : les objets Fabric sont imbriques (groupes,
 * calques) et `fontFamily` peut apparaitre a n'importe quelle profondeur.
 */
export function extractFontFamilies(node: unknown): string[] {
    const found = new Set<string>();

    const walk = (current: unknown) => {
        if (Array.isArray(current)) {
            current.forEach(walk);
            return;
        }
        if (!current || typeof current !== 'object') {
            return;
        }
        const record = current as Record<string, unknown>;
        const family = record.fontFamily;
        if (typeof family === 'string' && family.trim()) {
            const normalized = normalizeFamily(family);
            if (normalized) {
                found.add(normalized);
            }
        }
        Object.values(record).forEach(walk);
    };

    walk(node);
    return [...found];
}

/** Construit l'URL de fichier de police servie (proxy backend). */
function fontFileUrl(family: string): string {
    const source = getFamilySource(family);
    const params = new URLSearchParams({ family, source, weight: '', style: '' });
    return `${getBackendUrl()}/fonts/file?${params.toString()}`;
}

/**
 * Charge une famille via FontFace depuis le proxy backend `/fonts/file`. Aucune
 * requete vers Google : le backend resout/proxifie (RGPD). Resout aussi bien sur
 * succes que sur echec (une police manquante degrade le rendu, ne le bloque pas).
 */
function loadFamilyViaBackend(family: string): Promise<void> {
    return new Promise<void>((resolve) => {
        let settled = false;
        const done = () => {
            if (settled) {
                return;
            }
            settled = true;
            resolvedFamilies.add(family);
            inflightFamilies.delete(family);
            resolve();
        };
        const timer = setTimeout(done, FONT_LOAD_TIMEOUT_MS);
        const finish = () => {
            clearTimeout(timer);
            done();
        };

        try {
            if (typeof FontFace === 'undefined' || !document.fonts) {
                finish();
                return;
            }
            const face = new FontFace(family, `url("${fontFileUrl(family)}")`);
            face.load()
                .then((loaded) => {
                    (document.fonts as unknown as { add(f: FontFace): void }).add(loaded);
                    finish();
                })
                .catch((e) => {
                    console.warn('[fonts] chargement FontFace échoué', family, e);
                    finish();
                });
        } catch (e) {
            console.warn('[fonts] FontFace non supporté', family, e);
            finish();
        }
    });
}

/** Chemin LEGACY : chargement via WebFontLoader/Google direct (repli flag off). */
function loadFamilyViaGoogle(family: string): Promise<void> {
    return new Promise<void>((resolve) => {
        let settled = false;
        const done = () => {
            if (settled) {
                return;
            }
            settled = true;
            resolvedFamilies.add(family);
            inflightFamilies.delete(family);
            resolve();
        };
        const timer = setTimeout(done, FONT_LOAD_TIMEOUT_MS);
        const finish = () => {
            clearTimeout(timer);
            done();
        };
        WebFont.load({
            google: { families: [family] },
            active: finish,
            inactive: finish
        });
    });
}

/**
 * Garantit que les familles demandees sont utilisables par le canvas avant qu'il
 * ne soit rendu. Les polices systeme et les familles custom (deja FontFace'ees par
 * useCustomFonts) sont ignorees. Chargement via proxy backend (RGPD) ; repli
 * WebFont/Google si le flag est off.
 */
export async function ensureFontsLoaded(families: string[]): Promise<void> {
    const useBackend = fontLibraryEnabled();
    const pending = [...new Set(families.map(normalizeFamily).filter(Boolean))].filter((family) => {
        if (systemFontSet.has(family.toLowerCase())) {
            return false;
        }
        if (resolvedFamilies.has(family)) {
            return false;
        }
        if (customFamilies.has(family)) {
            return false;
        }
        // Backend : on tente TOUTE famille non-system (le proxy resout aussi les
        // Google Fonts hors catalogue). Legacy : on n'envoie a WebFont que ce que
        // Google connait (evite les 404), sauf si la liste n'est pas encore prete.
        if (useBackend) {
            return true;
        }
        return knownFamilies.size === 0 || knownFamilies.has(family);
    });

    const requests = pending.map((family) => {
        const inflight = inflightFamilies.get(family);
        if (inflight) {
            return inflight;
        }
        const request = useBackend ? loadFamilyViaBackend(family) : loadFamilyViaGoogle(family);
        inflightFamilies.set(family, request);
        return request;
    });

    await Promise.all(requests);
}

/**
 * Familles chargees avec succes ou definitivement ecartees. Permet aux appelants
 * (apercus du FontPicker) d'eviter de redemander ce qui est deja resolu.
 */
export function isFontResolved(family: string): boolean {
    const normalized = normalizeFamily(family);
    return systemFontSet.has(normalized.toLowerCase()) || resolvedFamilies.has(normalized);
}

function blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('read error'));
        reader.readAsDataURL(blob);
    });
}

/** Déduit un format FontFace/registerFont depuis le type MIME du fichier servi. */
function fontFormatFromMime(mime: string): string {
    const m = (mime || '').toLowerCase();
    if (m.includes('woff2')) {
        return 'woff2';
    }
    if (m.includes('woff')) {
        return 'woff';
    }
    if (m.includes('otf') || m.includes('opentype')) {
        return 'opentype';
    }
    return 'truetype';
}

/**
 * Résout une famille en data-URL via le proxy backend `/fonts/file` (pour l'export
 * serveur : toutes les polices utilisées sont jointes au ZIP). Renvoie `null` pour
 * les familles système/custom (déjà couvertes), si le flag est off, ou en cas
 * d'échec réseau (l'export ne doit jamais être bloqué par une police).
 */
export async function fetchFontAsDataUrl(
    family: string
): Promise<{ family: string; dataUrl: string; format: string } | null> {
    const name = normalizeFamily(family);
    if (!name || isSystemFamily(name) || isCustomFamily(name) || !fontLibraryEnabled()) {
        return null;
    }
    try {
        const resp = await fetch(fontFileUrl(name), { headers: tenantHeaders() });
        if (!resp.ok) {
            return null;
        }
        const blob = await resp.blob();
        const dataUrl = await blobToDataUrl(blob);
        return { family: name, dataUrl, format: fontFormatFromMime(blob.type) };
    } catch (e) {
        console.warn('[fonts] résolution data-URL échouée (ignorée)', name, e);
        return null;
    }
}
